import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';

const languages = [
  { code: 'en', native: 'English' },
  { code: 'hi', native: 'हिन्दी' },
  { code: 'gu', native: 'ગુજરાતી' },
  { code: 'ar', native: 'العربية' },
  { code: 'zh', native: '中文' },
  { code: 'es', native: 'Español' },
  { code: 'fr', native: 'Français' },
  { code: 'de', native: 'Deutsch' },
  { code: 'ru', native: 'Русский' },
  { code: 'ja', native: '日本語' },
  { code: 'ko', native: '한국어' },
  { code: 'pt', native: 'Português' },
  { code: 'it', native: 'Italiano' },
  { code: 'nl', native: 'Nederlands' },
  { code: 'tr', native: 'Türkçe' },
  { code: 'th', native: 'ไทย' },
  { code: 'vi', native: 'Tiếng Việt' },
  { code: 'id', native: 'Bahasa Indonesia' },
  { code: 'ms', native: 'Melayu' },
  { code: 'ur', native: 'اردو' }
];

export default function LanguageSwitcher() {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const listRef = useRef(null);
  const [moreBelow, setMoreBelow] = useState(false); // more languages hidden below the visible part of the list
  const current = languages.find((l) => l.code === lang) || languages[0];

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const checkScroll = useCallback(() => {
    const el = listRef.current;
    if (el) setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 6);
  }, []);
  useEffect(() => { if (open) checkScroll(); }, [open, checkScroll]);

  return (
    <div ref={ref} className="relative notranslate" translate="no" data-testid="language-switcher">
      <button
        data-testid="language-menu-btn"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-md border border-[color:var(--line)] bg-white text-xs font-medium text-[color:var(--ink)] hover:border-[color:var(--brand)] hover:text-[color:var(--brand)] transition-colors"
      >
        <Globe size={15} className="text-[color:var(--brand)] shrink-0" />
        <span className="max-w-[92px] truncate">{current.native}</span>
        <ChevronDown size={14} className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          ref={listRef}
          onScroll={checkScroll}
          data-testid="language-dropdown"
          className="lang-panel absolute right-0 top-full mt-2 w-[min(20rem,calc(100vw_-_1.5rem))] max-h-[70vh] overflow-y-auto bg-white border border-[color:var(--line)] rounded-md shadow-2xl p-1 z-[70]"
        >
          {/* two columns: all 20 languages are visible at once on most screens, no hidden list to discover */}
          <div className="grid grid-cols-2">
            {languages.map((l) => (
              <button
                key={l.code}
                data-testid={`language-option-${l.code}`}
                onClick={() => { setLang(l.code); setOpen(false); }}
                title={l.code.toUpperCase()}
                className={`min-w-0 text-left px-3 py-2 text-sm rounded hover:bg-[color:var(--brand-soft)] transition-colors ${lang === l.code ? 'text-[color:var(--brand)] font-semibold bg-[color:var(--brand-soft)]/60' : 'text-[color:var(--ink)]'}`}
              >
                <span className="block truncate">{l.native}</span>
              </button>
            ))}
          </div>
          {/* only on short screens where the list still scrolls: fade + arrow at the bottom, gone once you reach the end */}
          {moreBelow && (
            <div className="sticky bottom-0 -mb-1 pt-6 pb-1 flex justify-center pointer-events-none bg-gradient-to-t from-white via-white/90 to-transparent">
              <ChevronDown size={18} className="text-[color:var(--brand)] animate-bounce" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
